import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {getPanels} from '../panelsSelectors';
import {LegacyPanelId, PanelTypesEnum} from '../panelStateBase';
import {LegacyPanelState} from './legacyPanelState';

/*
 * Panel selectors.
 */

export interface LegacyPanelProps {
  panelId: LegacyPanelId;
}

export type LegacyPanelIdSelector<P> = AppParametricSelector<P, LegacyPanelId>;

function getLegacyPanelId(_state: unknown, props: LegacyPanelProps) {
  return props.panelId;
}

export type LegacyPanelSelector<P> = AppParametricSelector<P, LegacyPanelState>;

export function makeGetLegacyPanel(): LegacyPanelSelector<LegacyPanelProps>;
export function makeGetLegacyPanel<P>(getPanelId: LegacyPanelIdSelector<P>): LegacyPanelSelector<P>;
export function makeGetLegacyPanel(getPanelId: LegacyPanelIdSelector<any> = getLegacyPanelId) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    if (!panel || panel.type !== PanelTypesEnum.LEGACY) {
      throw new Error('Legacy panel not found.');
    }

    return panel;
  });
}

export function makeGetLegacyPath() {
  const getLegacyPanel = makeGetLegacyPanel();

  return createSelector(getLegacyPanel, ({path}) => path);
}

type OptionalPathSelector<P> = AppParametricSelector<P, string | undefined>;

export function makeGetOptionalLegacyPath(): OptionalPathSelector<LegacyPanelProps>;
export function makeGetOptionalLegacyPath<P>(getPanelId: LegacyPanelIdSelector<P>): OptionalPathSelector<P>;
export function makeGetOptionalLegacyPath(getPanelId: LegacyPanelIdSelector<any> = getLegacyPanelId) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    if (!panel) {
      return undefined;
    }

    if (panel.type !== PanelTypesEnum.LEGACY) {
      throw new Error('Expected panel to be legacy panel.');
    }

    return panel.path;
  });
}
